export type ParsedParam = {
  name: string;
  required: boolean;
  type:
    | "string"
    | "number"
    | "integer"
    | "boolean"
    | "array"
    | "object"
    | "unknown";
  description?: string;
  enum?: Array<string | number | boolean>;
  itemsType?: ParsedParam["type"];
  defaultValue?: unknown;
  minimum?: number;
  maximum?: number;
};

type JsonSchema = {
  type?: string | string[];
  description?: string;
  enum?: Array<string | number | boolean>;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  items?: JsonSchema;
  anyOf?: JsonSchema[];
  oneOf?: JsonSchema[];
  allOf?: JsonSchema[];
  $ref?: string;
  // Allow extra fields without typing
  [key: string]: unknown;
};

function normalizeType(schema?: JsonSchema): ParsedParam["type"] {
  if (!schema) {
    return "unknown";
  }
  const t = schema.type;
  const first = Array.isArray(t) ? t[0] : t;
  if (first === "string" || first === "number" || first === "integer") {
    return first;
  }
  if (first === "boolean" || first === "array" || first === "object") {
    return first;
  }
  // Fallbacks with combinators
  if (schema.anyOf?.length) {
    return normalizeType(schema.anyOf[0]);
  }
  if (schema.oneOf?.length) {
    return normalizeType(schema.oneOf[0]);
  }
  if (schema.allOf?.length) {
    return normalizeType(schema.allOf[0]);
  }
  return "unknown";
}

export function parsePromptArguments(prompt: unknown): ParsedParam[] {
  if (!prompt || typeof prompt !== "object") {
    return [];
  }

  const p = prompt as { arguments?: unknown[] };
  if (!Array.isArray(p.arguments)) {
    return [];
  }

  return p.arguments.map((arg): ParsedParam => {
    const argument = arg as {
      name?: string;
      description?: string;
      required?: boolean;
      type?: string;
      enum?: Array<string | number | boolean>;
      default?: unknown;
      minimum?: number;
      maximum?: number;
    };

    return {
      name: argument.name || "",
      required: argument.required ?? false,
      type: (argument.type as ParsedParam["type"]) || "string", // Default to string for prompts
      description: argument.description,
      enum: argument.enum,
      defaultValue: argument.default,
      minimum: argument.minimum,
      maximum: argument.maximum,
    };
  });
}

export function parseToolInputSchema(schema: unknown): ParsedParam[] {
  if (!schema || typeof schema !== "object") {
    return [];
  }
  const s = schema as JsonSchema;

  // Prefer object with properties at the top level
  if (s.type === "object" && s.properties && typeof s.properties === "object") {
    const required = new Set<string>(s.required || []);
    return Object.entries(s.properties).map(([name, prop]): ParsedParam => {
      const type = normalizeType(prop);
      const param: ParsedParam = {
        name,
        required: required.has(name),
        type,
        description: prop.description,
      };
      if (Array.isArray(prop.enum)) {
        param.enum = prop.enum;
      }
      const pExt = prop as JsonSchema & {
        default?: unknown;
        minimum?: number;
        maximum?: number;
      };
      if (pExt.default !== undefined) {
        param.defaultValue = pExt.default;
      }
      if (typeof pExt.minimum === "number") {
        param.minimum = pExt.minimum;
      }
      if (typeof pExt.maximum === "number") {
        param.maximum = pExt.maximum;
      }
      if (type === "array") {
        param.itemsType = normalizeType(prop.items as JsonSchema);
      }
      return param;
    });
  }

  // If it's not an object schema, treat the whole schema as a single unnamed param
  const type = normalizeType(s);
  return [
    {
      name: "input",
      required: true,
      type,
      description: s.description,
      enum: Array.isArray(s.enum) ? s.enum : undefined,
      itemsType: s.items ? normalizeType(s.items) : undefined,
      defaultValue: (s as JsonSchema & { default?: unknown }).default,
      minimum: (s as JsonSchema & { minimum?: number }).minimum,
      maximum: (s as JsonSchema & { maximum?: number }).maximum,
    },
  ];
}
